import { z } from "zod";

const isoDate = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ");

const optionalText = (max: number, message: string) =>
  z
    .string()
    .trim()
    .max(max, message)
    // `null` et jamais `undefined` : « pas de référence » est un état, pas un
    // champ omis. Un `undefined` qui traverserait jusqu'à l'écriture laisserait
    // l'ancienne valeur en place, et vider un champ n'aurait aucun effet.
    .transform((v) => (v === "" ? null : v))
    .nullish()
    .transform((v) => v ?? null);

export const listDeadlinesInput = z.object({
  confirmationId: z.string().optional(),
  /**
   * Inclut ce qui est déjà passé. Vrai par défaut, et c'est délibéré : une
   * décennale expirée est précisément ce qu'il faut voir, pas ce qu'il faut
   * masquer.
   */
  includePast: z.boolean().default(true),
});

export const saveDeadlineInput = z.object({
  confirmationId: z.string().optional(),
  /** Absent à la création. */
  id: z.string().uuid().optional(),
  kind: z.enum(["insurance", "tax", "certification", "retention", "other"]).default("other"),
  label: z
    .string()
    .trim()
    .min(1, "Un intitulé est attendu")
    .max(120, "Intitulé trop long"),
  dueOn: isoDate,
  amount: z
    .union([z.literal(""), z.null(), z.undefined(), z.coerce.number()])
    .transform((v) => (v === "" || v == null ? null : v))
    .refine(
      (v) => v == null || (Number.isFinite(v) && v >= 0),
      "Un montant ne peut pas être négatif",
    )
    .transform((v) => (v == null ? null : Math.round(v * 100) / 100)),
  reference: optionalText(120, "Référence trop longue"),
  notes: optionalText(2000, "Notes trop longues"),
  recurrence: z.enum(["none", "monthly", "quarterly", "yearly"]).default("none"),
  projectId: z.string().uuid().nullish().transform((v) => v ?? null),
});

export const removeDeadlineInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid("Identifiant d'échéance attendu"),
});

/**
 * Reporter une échéance honorée sur sa date suivante.
 *
 * Un verbe à part plutôt qu'un `enregistrer` avec une nouvelle date : c'est le
 * geste courant — « c'est payé, à quand la prochaine ? » — et lui donner son
 * propre nom permet à l'écran de proposer la date suivante au lieu de la faire
 * calculer de tête. Il refuse une échéance ponctuelle, qui n'a pas de suivante.
 */
export const rollDeadlineInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid("Identifiant d'échéance attendu"),
});

export type ListDeadlinesInput = z.infer<typeof listDeadlinesInput>;
export type SaveDeadlineInput = z.infer<typeof saveDeadlineInput>;
export type SaveDeadlineFormInput = z.input<typeof saveDeadlineInput>;
export type RemoveDeadlineInput = z.infer<typeof removeDeadlineInput>;
export type RollDeadlineInput = z.infer<typeof rollDeadlineInput>;
